import { useMutation, useQueryClient } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { useId, useState } from "react"

import { ActivityMemberPicker } from "@/components/activity-levels/member-picker"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "@/components/ui/toast"
import { getRoleLabel } from "@/core/guild-events/labels"
import { type WwmEventRoleKey } from "@/core/guild-events/types"
import { guildEventsApi } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"
import {
  useGuildEventsDetailRouteStore,
  useRsvpOverrideForm,
} from "@/lib/stores/guild-events-admin-detail-store"

const routeApi = getRouteApi("/_authed/_admin/guild-events/$eventId")

export function ManualRsvpOverride({
  eventId,
  signups,
  eventRoles,
}: {
  eventId: string
  signups: Array<{ memberId: string; guildDisplayName: string }>
  eventRoles: WwmEventRoleKey[]
}) {
  const navigate = routeApi.useNavigate()
  const search = routeApi.useSearch()
  const queryClient = useQueryClient()
  const { store, actions } = useGuildEventsDetailRouteStore()
  const rsvpOverride = useRsvpOverrideForm({ store, actions })

  const memberIdInputId = useId()
  const rsvpStateInputId = useId()
  const roleKeyInputId = useId()
  const memberIdErrorId = useId()

  const [memberError, setMemberError] = useState<string | null>(null)
  const memberIdValue = search.memberId ?? ""

  const canPickRole = rsvpOverride.rsvpState === "attending"

  const overrideMutation = useMutation({
    mutationFn: async () => {
      const memberId = memberIdValue.trim()
      if (!memberId) {
        throw new Error("A member is required for manual override.")
      }
      await guildEventsApi.overrideSignup(eventId, memberId, {
        state: rsvpOverride.rsvpState,
        roleKey: canPickRole ? rsvpOverride.roleKey || null : null,
      })
    },
    onSuccess: async () => {
      setMemberError(null)
      toast.success("Override applied")
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => {
      setMemberError(error.message)
      toast.error(error.message)
    },
  })

  return (
    <Card>
      <CardContent className="grid gap-3 pt-5 md:grid-cols-2">
        <div className="text-sm font-semibold md:col-span-2">
          Manual RSVP Override
        </div>
        <div className="md:col-span-2">
          <ActivityMemberPicker
            id="member-search"
            label="Add member"
            onSelect={(member) => {
              actions.setMemberIdInput(member.memberId)
              setMemberError(null)
              void navigate({
                search: { memberId: member.memberId },
                replace: true,
              })
            }}
          />
        </div>
        <div className="grid gap-2 md:col-span-2">
          <label
            htmlFor={memberIdInputId}
            className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
          >
            Member
          </label>
          <Select
            value={memberIdValue}
            onValueChange={(value) => {
              actions.setMemberIdInput(value ?? "")
              setMemberError(null)
              void navigate({
                search: { memberId: value || undefined },
                replace: true,
              })
            }}
          >
            <SelectTrigger
              id={memberIdInputId}
              aria-invalid={memberError ? true : undefined}
              aria-describedby={memberError ? memberIdErrorId : undefined}
            >
              <SelectValue placeholder="Select a member" />
            </SelectTrigger>
            <SelectContent>
              {signups.map((signup) => (
                <SelectItem key={signup.memberId} value={signup.memberId}>
                  {signup.guildDisplayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {memberError ? (
            <p
              id={memberIdErrorId}
              className="text-sm text-destructive"
              role="alert"
            >
              {memberError}
            </p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <label
            htmlFor={rsvpStateInputId}
            className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
          >
            RSVP State
          </label>
          <Select
            value={rsvpOverride.rsvpState}
            onValueChange={(next) => {
              if (!next) {
                return
              }
              actions.setRsvpState(guildEventsApi.parseRsvpState(next))
            }}
          >
            <SelectTrigger id={rsvpStateInputId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="attending">Attending</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="not_attending">Not Attending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <label
            htmlFor={roleKeyInputId}
            className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
          >
            Role Key
          </label>
          <Select
            value={canPickRole ? rsvpOverride.roleKey : ""}
            onValueChange={(next) =>
              actions.setRoleKey((next ?? "") as WwmEventRoleKey | "")
            }
            disabled={!canPickRole}
          >
            <SelectTrigger id={roleKeyInputId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {eventRoles.map((eventRole) => (
                <SelectItem key={eventRole} value={eventRole}>
                  {getRoleLabel(eventRole)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <Button
            type="button"
            onClick={() => overrideMutation.mutate()}
            disabled={overrideMutation.isPending}
          >
            {overrideMutation.isPending ? "Saving..." : "Apply Override"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
